import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { INumberSlice } from "@/redux/slices/numbers/types";

interface IGetNumbersProps {
  areaCode: string | number;
  pattern?: string;
  type: string;
  countryCode: string | number;
  limit?: number;
  page?: number;
  includePrice?: boolean;
}

export const getNumbers = createAsyncThunk(
  "numbers/getNumbers",
  async ({
    areaCode = 20,
    pattern = "**",
    limit = 20,
    page = 1,
    type = "standard",
    countryCode = 44,
    includePrice = true,
  }: IGetNumbersProps) => {
    const getStandard = () =>
      axios({
        method: "GET",
        url: `/api/voice/number-search`,
        params: {
          area_code: areaCode,
          pattern,
          limit,
          page,
          type: "standard",
          country_code: countryCode,
          include_price: includePrice ? "yes" : "no",
        },
      }).then((res) => {
        const { page, total_count, total_pages, result: numbers } = res.data;

        return {
          page,
          total_count,
          total_pages,
          numbers,
        };
      });

    const getPremium = () =>
      axios({
        method: "GET",
        url: `/api/voice/number-search`,
        params: {
          area_code: areaCode,
          pattern,
          limit,
          page,
          type: "premium",
          country_code: countryCode,
          include_price: includePrice ? "yes" : "no",
        },
      }).then((res) => {
        const { page, total_count, total_pages, result: numbers } = res.data;

        return {
          page,
          total_count,
          total_pages,
          numbers,
        };
      });

    const getNonGeographic = () =>
      axios({
        method: "GET",
        url: `/api/voice/number-search`,
        params: {
          area_code: "0330",
          pattern,
          limit,
          page,
          type,
          country_code: countryCode,
          include_price: includePrice ? "yes" : "no",
        },
      }).then((res) => {
        const { page, total_count, total_pages, result: numbers } = res.data;

        return {
          page,
          total_count,
          total_pages,
          numbers,
        };
      });

    // return as object with {standard: ... || null, premium: ... || null}
    switch (type) {
      case "standard":
        return {
          standard: await getStandard(),
          premium: null,
          nonGeo: null,
        };
      case "premium":
        return {
          standard: null,
          premium: await getPremium(),
          nonGeo: null,
        };
      case "nonGeo":
        return {
          standard: null,
          premium: null,
          nonGeo: await getNonGeographic(),
        };
      default:
        return {
          standard: await getStandard(),
          premium: await getPremium(),
          nonGeo: await getNonGeographic(),
        };
    }
  }
);

const pending = (state: INumberSlice, { meta: { arg } }) => {
  switch (arg.type) {
    case "all":
      state.numbers.standard.page = arg.page || 1;
      state.numbers.premium.page = arg.page || 1;
      state.numbers.nonGeo.page = arg.page || 1;
      state.numbers.nonGeo.loading = true;
      state.numbers.standard.loading = true;
      state.numbers.premium.loading = true;
      break;
    case "standard":
      state.numbers.standard.page = arg.page || 1;
      state.numbers.standard.loading = true;
      break;
    case "premium":
      state.numbers.premium.page = arg.page || 1;
      state.numbers.premium.loading = true;
      break;
    case "nonGeo":
      state.numbers.nonGeo.page = arg.page || 1;
      state.numbers.nonGeo.loading = true;
      break;
  }
};

const fulfilled = (state: INumberSlice, { payload }) => {
  if (payload.standard) {
    state.numbers.standard = {
      ...payload.standard,
      loading: false,
    };
  }
  if (payload.premium) {
    state.numbers.premium = {
      ...payload.premium,
      loading: false,
    };
  }
  if (payload.nonGeo) {
    state.numbers.nonGeo = {
      ...payload.nonGeo,
      loading: false,
    };
  }
};

const rejected = (state: INumberSlice) => {
  state.numbers = {
    standard: {
      loading: false,
      page: 0,
      total_count: 0,
      total_pages: 0,
      numbers: [],
    },
    premium: {
      loading: false,
      page: 0,
      total_count: 0,
      total_pages: 0,
      numbers: [],
    },
    nonGeo: {
      loading: false,
      page: 0,
      total_count: 0,
      total_pages: 0,
      numbers: [],
    },
  };
};

export default {
  action: getNumbers,
  pending,
  fulfilled,
  rejected,
};
